import hasAlreadyConsented from "../../../hasAlreadyConsented";

export default class YoutubePlayer {
    static init() {
        const isNeosBackend = document.body.classList.contains('neos-backend');
        if (isNeosBackend) {
            return;
        }

        let elements = document.querySelectorAll('.youtube-player');
        if (!elements.length) {
            return;
        }

        Array.from(elements).forEach(el => {
            YoutubePlayer.playersList.push(new YoutubePlayer(el));
        });

        // load scripts delayed to not block the critical path
        window.addEventListener('load', () => {
            setTimeout(() => YoutubePlayer.loadScript(), 300);
        });
    }

    static jsIsLoaded = false;

    static loadScript() {
        if (YoutubePlayer.jsIsLoaded || document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
            return;
        }
        let tag = document.createElement('script');
        let firstScriptTag = document.getElementsByTagName('script')[0];

        tag.src = 'https://www.youtube.com/iframe_api';
        if (firstScriptTag) {
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        } else {
            document.head.append(tag);
        }
        window.onYouTubeIframeAPIReady = () => YoutubePlayer.jsIsLoaded = true
    }

    static classes = {
        node: 'youtube-player',
        get overlay() {
            return this.node + '__overlay'
        },
        get iframe() {
            return this.node + '__iframe'
        },
        state: {
            get initialised() {
                return YoutubePlayer.classes.node + '--is-initialized'
            },
            get unstarted() {
                return YoutubePlayer.classes.node + '--is-unstarted'
            },
            get ended() {
                return YoutubePlayer.classes.node + '--is-ended'
            },
            get playing() {
                return YoutubePlayer.classes.node + '--is-playing'
            },
            get paused() {
                return YoutubePlayer.classes.node + '--is-paused'
            },
            get buffering() {
                return YoutubePlayer.classes.node + '--is-buffering'
            },
            get cued() {
                return YoutubePlayer.classes.node + '--is-cued'
            }
        }
    }

    /**
     * Public accessible list of all YouTube players on the page
     */
    static playersList = [];

    constructor(element) {
        this.options = {};
        this.element = element;
        this.iframe = element.querySelector('.' + YoutubePlayer.classes.iframe);
        this.overlay = element.querySelector('.' + YoutubePlayer.classes.overlay);
        this.state = 'unstarted';
        this.isInitialised = false;
        this.id = this.iframe.dataset.id;
        this.playlist = this.iframe.dataset.playlist;

        this.overlay.addEventListener('click', () => {
            if (!hasAlreadyConsented()) {
                alert('Bitte akzeptiere im Cookie Banner YouTube um das Video zu sehen.');
                window.Clickskeks.showConsent();
                return;
            }
            this.startPlayerOnReady();
        });

        window.addEventListener('videoStop', (e) => {
            let player = YoutubePlayer.getPlayerById(e.detail.videoId);
            player.YTStop();
        });

        window.addEventListener('videoPlay', (e) => {
            let player = YoutubePlayer.getPlayerById(e.detail.videoId);
            player.startPlayerOnReady();
        });
    }

    /** Returns a YouTubePlayer instance by id */
    static getPlayerById(id) {
        return YoutubePlayer.playersList.find(player => player.id === id);
    }

    isPlaylist() {
        return this.player.getPlaylist !== null;
    }

    getPlaylist() {
        return this.player.getPlaylist();
    }

    getPlaylistIndex() {
        return this.player.getPlaylistIndex();
    }

    startPlayerOnReady() {
        if (!YoutubePlayer.jsIsLoaded) {
            setTimeout(() => this.startPlayerOnReady(), 100);
            return;
        }

        if (this.isInitialised) {
            this.YTPlay();
        } else {
            this.setState('initialised');
            this.isInitialised = true;
            this.player = this.createPlayer(this.iframe);
        }
    }

    createPlayer(playerNode) {
        this.options = {
            origin: this.iframe.dataset.origin,
            hl: this.iframe.dataset.language,
            iv_load_policy: 3,
            modestbranding: 1,
            enablejsapi: 1,
            playsinline: 1,
            autohide: 1,
            controls: 0,
            playerVars: {rel: 0, showinfo: 0, ecver: 2}
        }

        if (this.id) {
            this.options.videoId = this.id;
        }
        if (this.playlist) {
            this.options.playlist = this.playlist;
            this.options.playerVars.list = this.playlist;
            this.options.playerVars.listType = 'playlist'
        }

        return new YT.Player(playerNode, {
            events: {
                'onReady': () => {
                    if (this.playlist) {
                        this.player.cuePlaylist({list: this.playlist}); //loads and cues list .
                    }

                    this.YTPlay();
                },
                'onStateChange': (event) => {
                    switch (event.data) {
                        case -1:
                            this.setState('unstarted');
                            break;
                        case 0:
                            this.setState('ended');
                            break;
                        case 1:
                            this.setState('playing');
                            break;
                        case 2:
                            this.setState('paused');
                            break;
                        case 3:
                            this.setState('buffering');
                            break;
                        case 5:
                            this.setState('cued');
                            break;
                    }

                    this.element.dispatchEvent(new CustomEvent('stateChange', {detail: this.getState()}));
                }
            },
            ...this.options
        });
    }

    setState(state) {
        let stateClass = YoutubePlayer.classes.state[state];
        if (this.element.classList.contains(stateClass)) return;

        for (let key in YoutubePlayer.classes.state) {
            if (key !== 'initialised') {
                this.element.classList.remove(YoutubePlayer.classes.state[key]);
            }
        }
        this.state = state;
        this.element.classList.add(stateClass);
    }

    getState() {
        return this.state;
    }

    YTPlay() {
        let play = () => {
            if (typeof this.player.playVideo !== 'function') {
                setTimeout(() => play(), 100);
                return;
            }

            this.player.playVideo();
            this.setState('playing');
        }
        play();
    }

    YTPause() {
        let pause = () => {
            if (typeof this.player.pauseVideo !== 'function') {
                setTimeout(() => pause(), 100);
                return;
            }

            this.player.pauseVideo();
            this.setState('paused');
        }
        pause();
    }

    YTStop() {
        let stop = () => {
            if (typeof this.player.stopVideo !== 'function') {
                setTimeout(() => stop(), 100);
                return;
            }

            this.player.stopVideo();
            this.setState('stopped');
        }
        stop();
    }
}
